import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { PermissionsProvider, usePermissions } from "@/context/PermissionsContext";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Quotations from "@/pages/Quotations";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Employees from "@/pages/Employees";
import Roadmap from "@/pages/Roadmap";
import Settings from "@/pages/Settings";
import ComingSoon from "@/pages/ComingSoon";
import QuotationDetail from "@/pages/QuotationDetail";
import Handovers, { HandoverDetail } from "@/pages/Handovers";
import Invoices, { InvoiceDetail } from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import AuditLogs from "@/pages/AuditLogs";
import PublicApproval from "@/pages/PublicApproval";
import OperationsHome, { Workload, Revisions, Deliveries } from "@/pages/Operations";
import Branding from "@/pages/admin/Branding";
import ProductionSettings from "@/pages/admin/ProductionSettings";
import Changelog from "@/pages/admin/Changelog";
import ResetData from "@/pages/admin/ResetData";
import BackupRestore from "@/pages/admin/BackupRestore";
import Departments from "@/pages/admin/Departments";
import Designations from "@/pages/admin/Designations";
import Branches from "@/pages/admin/Branches";
import RolesPermissions from "@/pages/admin/RolesPermissions";
import CompaniesDivisions from "@/pages/admin/CompaniesDivisions";
import MyTasks from "@/pages/MyTasks";
import Profile from "@/pages/Profile";
import { ScopeProvider } from "@/context/ScopeContext";
import "@/index.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50" data-testid="loading-screen">
        <div className="text-sm text-zinc-500 font-mono">Loading Dream Home ERP…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // Force password change on first login before allowing anything else
  const path = location?.pathname || "";
  if (user.must_change_password && path !== "/app/profile" && !path.startsWith("/login")) {
    return <Navigate to="/app/profile?force=1" replace />;
  }
  return children;
}

/**
 * Milestone 2 · route-level permission guard. Wraps a route element and
 * redirects the user back to /app (dashboard) if they lack the required
 * module permission. Prevents URL-typing bypass of the sidebar filter.
 */
function Guard({ module, action = "view", children }) {
  const { has, loading, perms } = usePermissions();
  // While perms load, render the child (auth already passed). Once loaded, gate.
  if (loading || perms === null) return children;
  if (!has(module, action)) {
    return (
      <div className="max-w-xl mx-auto mt-24 px-6 text-center" data-testid="access-denied">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-xs mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
          Access denied
        </div>
        <h1 className="text-2xl font-semibold mb-2">You don't have permission to view this module.</h1>
        <p className="text-sm text-zinc-500">Ask an administrator to grant you <span className="font-mono">{action}</span> access to <span className="font-mono">{module}</span> — via <span className="font-mono">Administration → Roles &amp; Permissions</span>.</p>
        <div className="mt-6">
          <a href="/app" className="text-sm underline text-zinc-700">Go back to Dashboard</a>
        </div>
      </div>
    );
  }
  return children;
}

function RootRouter() {
  const location = useLocation();
  // Synchronous check — handle Google OAuth session_id fragment before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/approve/:id" element={<PublicApproval />} />
      <Route
        path="/app"
        element={
          <Protected>
            <AppShell />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="leads" element={<Guard module="leads"><Leads /></Guard>} />
        <Route path="clients" element={<Guard module="clients"><Clients /></Guard>} />
        <Route path="clients/:id" element={<Guard module="clients"><ClientDetail /></Guard>} />
        <Route path="quotations" element={<Guard module="quotations"><Quotations /></Guard>} />
        <Route path="projects" element={<Guard module="projects"><Projects /></Guard>} />
        <Route path="projects/:id" element={<Guard module="projects"><ProjectDetail /></Guard>} />
        <Route path="employees" element={<Guard module="employees"><Employees /></Guard>} />
        <Route path="quotations/:qid" element={<Guard module="quotations"><QuotationDetail /></Guard>} />
        <Route path="handovers" element={<Guard module="handovers"><Handovers /></Guard>} />
        <Route path="handovers/:id" element={<Guard module="handovers"><HandoverDetail /></Guard>} />
        <Route path="handover-inbox" element={<Guard module="handovers"><Handovers inbox /></Guard>} />
        <Route path="invoices" element={<Guard module="invoices"><Invoices /></Guard>} />
        <Route path="invoices/:id" element={<Guard module="invoices"><InvoiceDetail /></Guard>} />
        <Route path="payments" element={<Guard module="payments"><Payments /></Guard>} />
        <Route path="audit" element={<Guard module="audit_log"><AuditLogs /></Guard>} />
        <Route path="roadmap" element={<Roadmap />} />
        <Route path="settings" element={<Settings />} />
        <Route path="ai" element={<ComingSoon title="AI Studio" description="Quotation Assistant, Email Assistant, Production Analysis, Revenue Forecast — Sprint 7 (Emergent LLM: Claude Sonnet 4.5 / GPT-5.2 / Gemini 3)." />} />
        <Route path="resource-planning" element={<ComingSoon title="Resource Planning" description="Per-project resource plan: roles × hours × software × discipline × weeks. Must be approved before Team Assignment. Sprint 3." />} />
        <Route path="operations" element={<Guard module="projects"><OperationsHome /></Guard>} />
        <Route path="workload" element={<Guard module="projects"><Workload /></Guard>} />
        <Route path="revisions" element={<Guard module="revisions"><Revisions /></Guard>} />
        <Route path="deliveries" element={<Guard module="deliveries"><Deliveries /></Guard>} />
        <Route path="administration/branding" element={<Guard module="branding"><Branding /></Guard>} />
        <Route path="administration/departments" element={<Guard module="directory"><Departments /></Guard>} />
        <Route path="administration/designations" element={<Guard module="directory"><Designations /></Guard>} />
        <Route path="administration/branches" element={<Guard module="directory"><Branches /></Guard>} />
        <Route path="administration/roles" element={<Guard module="system_admin"><RolesPermissions /></Guard>} />
        <Route path="administration/companies" element={<Guard module="companies_admin"><CompaniesDivisions /></Guard>} />
        <Route path="my-tasks" element={<Guard module="my_tasks"><MyTasks /></Guard>} />
        <Route path="administration/production" element={<Guard module="system_admin"><ProductionSettings /></Guard>} />
        <Route path="administration/changelog" element={<Changelog />} />
        <Route path="administration/reset-data" element={<Guard module="system_admin"><ResetData /></Guard>} />
        <Route path="administration/backup-restore" element={<Guard module="system_admin"><BackupRestore /></Guard>} />
        <Route path="profile" element={<Profile />} />
        <Route path="tasks" element={<ComingSoon title="Tasks" description="Kanban task management, dependencies and timers per project. Sprint 3." />} />
        <Route path="qa" element={<ComingSoon title="Quality Assurance" description="QA sign-offs live inside each project. Global dashboard coming next." />} />
        <Route path="recruitment" element={<ComingSoon title="Recruitment" description="Job openings, candidates, interview pipeline. Sprint 4." />} />
        <Route path="payroll" element={<ComingSoon title="Payroll" description="Monthly payroll (IN + generic), payslips, statutory registers. Sprint 5." />} />
        <Route path="vendors" element={<ComingSoon title="Vendors" description="Vendor master, POs, office expenses, compliance. Sprint 5." />} />
        <Route path="assets" element={<ComingSoon title="Asset Lifecycle" description="Full lifecycle: procurement → provisioning → utilization → maintenance → depreciation → disposal. Includes reality-capture kits (Matterport, drones, laser scanners) and software licenses. Sprint 5." />} />
        <Route path="documents" element={<ComingSoon title="Documents" description="Cross-cutting document management with versioning per client / project / quotation / revision. Sprint 5." />} />
        <Route path="client-portal" element={<ComingSoon title="Client Portal" description="External tokenized portal — quotation approvals, revisions review, deliverables, Matterport/Point Cloud links, invoices. Sprint 6." />} />
        <Route path="employee-portal" element={<ComingSoon title="Employee Portal" description="Internal self-service — my hours, leaves, payslips, assets, documents. Sprint 6." />} />
        <Route path="reports" element={<ComingSoon title="Reports Studio" description="Canned + custom reports: P&L per project, utilization by discipline, AR aging, software-license usage. Sprint 4." />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <PermissionsProvider>
          <ScopeProvider>
            <BrowserRouter>
              <RootRouter />
              <Toaster position="top-right" richColors closeButton />
            </BrowserRouter>
          </ScopeProvider>
        </PermissionsProvider>
      </AuthProvider>
    </BrandingProvider>
  );
}
